import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ShareAuthType } from "./ShareScreenDrawer";
import { getErrorMessage } from "../../../utils/GeneralUtils";
import SharedScreenAPI from "../../../api/api/SharedScreenAPI";
import { useAtomValue } from "jotai";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import { useParams } from "react-router-dom";

interface Props {
  viewID: string | undefined;
  authentication: ShareAuthType;
  password: string;
  recordID?: string;
  recordModule?: string;
  onSuccess: (sharedViewID: string) => void;
  onError: (err: string) => void;
  parentPageID?: string; // Allows for View metadata to be updated
  parentViewID?: string; // Allows for SubView metadata to be updated
  documentID?: string;
}

// For Sharing Pages, Views, AND Documents.
export default function useCreateSharedScreen() {
  const workspaceID = useAtomValue(workspaceIdAtom);
  const client = useQueryClient();
  const { tenantID } = useParams();

  const createSharedScreen = useMutation({
    mutationFn: ({
      viewID,
      authentication,
      password,
      recordID,
      recordModule,
      parentPageID,
      parentViewID,
      documentID,
    }: Props) => {
      if (!workspaceID) return Promise.reject("Missing workspace ID!");

      if (!documentID && !parentPageID && !parentViewID) {
        return Promise.reject("A parent page or view is required to share.");
      }

      return SharedScreenAPI.createSharedScreen(
        workspaceID,
        documentID || viewID,
        authentication,
        password,
        recordID,
        recordModule,
        parentPageID,
        parentViewID,
        documentID
      ).then((data) => data.data.data);
    },
    onSuccess: (
      data,
      { onSuccess, viewID, recordID, documentID, parentPageID }
    ) => {
      const {
        sharedView,
        sharedPage,
        sharedDocument,
        updatedPage,
        updatedView,
      } = data || {};

      // A View was shared
      if (sharedView) {
        client.setQueryData(["sharedView", viewID, recordID], {
          ...sharedView,
          exists: true,
        });
      }

      // A Page was shared
      if (sharedPage) {
        client.setQueryData(["sharedPage", parentPageID], {
          ...sharedPage,
          exists: true,
        });

        client.setQueryData(["page", parentPageID], (prev: any) => ({
          ...(prev || {}),
          Shared: true,
        }));
      }

      if (sharedDocument) {
        client.setQueryData(["sharedDocument", documentID], {
          ...sharedDocument,
          exists: true,
        });

        client.setQueryData(
          [tenantID, "record", "Documents", documentID],
          (prev) => ({ ...(prev || {}), Shared: true })
        );

        client.setQueriesData(
          {
            queryKey: [tenantID, "records", "Documents"],
          },
          (prev: any) => {
            if (!prev?.records?.length) return prev;

            const newDocs = prev.records.map((doc: any) => {
              if (doc._id === documentID) return { ...doc, Shared: true };
              return doc;
            });

            return { ...prev, records: newDocs };
          }
        );
      }

      // If a view was shared, that means the page's view metadata was also updated
      if (updatedPage) {
        client.setQueryData(["page", updatedPage._id], updatedPage);
      }
      // If a subview was shared, that means the view's subView metadata was also updated
      if (updatedView) {
        client.setQueryData(["view", updatedView._id], updatedView);
      }

      if (sharedDocument) {
        return onSuccess(sharedDocument?._id);
      }

      onSuccess(sharedView?._id || sharedPage?._id);
    },
    onError: (e, { onError }) => {
      console.log(e);
      onError(getErrorMessage(e));
    },
  });

  return {
    createSharedScreen: createSharedScreen.mutate,
    creatingScreen: createSharedScreen.isPending,
  };
}
